import React, { useEffect, useReducer, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Skeleton,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Checkbox,
  ListItemText
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRecoilState } from "recoil";
import { parksState } from "../../atoms/parks";
import { userState } from "../../atoms/user";
import { tokenState } from "../../atoms/token";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import APIService from "../../services/api.service";
import { CacheService } from "../../common/services/cache.service";
import APIOptions from "../../models/apiOptions";
import environment from "../../environment/environment";
import { Asset } from "../../models/asset";

// Map short codes to labels and IDs
const DOC_TYPES = [
  { code: "ARG", label: "Archaeology", id: 5 },
  { code: "PUB", label: "Public",        id: 1 },
  { code: "RES", label: "Restricted",    id: 2 },
  { code: "SNS", label: "Sensitive",     id: 3 },
  { code: "WRK", label: "WRK",           id: 4 }
];

export function UserUploadDialog({
  row,
  handleUnRejectClick,
  handleSaveClick,
  handleRejectUploadDialogClick,
  handleCloseClick
}) {
  const navigate = useNavigate();
  const [parks] = useRecoilState(parksState);
  const [userInfo] = useRecoilState(userState);
  const [token] = useRecoilState(tokenState);

  // form state reducer
  const [form, dispatch] = useReducer((state, updates) => ({ ...state, ...updates }), {});

  const [parent, setParent] = useState(null);
  const [securityTypes, setSecurityTypes] = useState([]);

  // Load security types, parent asset, and initialize form
  useEffect(() => {
    // fetch security types
    let url = `${environment.npgAPI}list?id=eticdoctype&format=JSON`;
    const apiService = new APIService<any>(axios, new CacheService, url, token);

    apiService.getAnyAsync().then((res:any) => {
      setSecurityTypes(res.map((item) => ({ label: item.description, value: item.label, id: item.id })));
    });

    // set initial form values
    dispatch(row);
    const parkObj = parks.find(p => p.name === row.parkName);
    if (parkObj) {
      dispatch({ parkName: parkObj.name, parkCode: parkObj.code });
      dispatch({ parkPreviousName: parkObj.previousName, parkRegionName: parkObj.regionName });
    }
  }, [row, parks, token]);

  // Handler for generic form field updates
  const handleChange = (field, value) => {
    const update = { [field]: value };

    // handle docType mapping
    if (field === "docType") {
      const typeObj = DOC_TYPES.find(dt => dt.code === value);
      if (typeObj) {
        update["ticDocTypeId"] = typeObj.id;
        update["docTypeId"] = typeObj.id;
      }
    }

    // handle ParkName -> ParkCode
    if (field === "parkName") {
      const parkObj = parks.find(p => p.name === value);
      if (parkObj) update["ParkCode"] = parkObj.code;
    }

    dispatch(update);
  };

  // Utility: navigate or show image
  const handleImageClick = (assetId) => {
    const roleMatch = form.docType.toLowerCase();
    const hasAccess =
      form.docType === "Public" ||
      (form.docType === "Sensitive" && userInfo.roles.includes("etic_sensitive_view")) ||
      (form.docType === "Archaeology" && userInfo.roles.includes("etic_archaeology_view")) ||
      form.docType === "Restricted";

    if (!hasAccess) {
      navigate('/SecurityGuidelines');
    } else if (assetId) {
      window.open(`https://npgallery.nps.gov/GetAsset/${assetId}`);
    }
  };

  // Render images list if available
  const images = useMemo(() => parent?.children || [], [parent]);

  return (
    <Dialog open PaperProps={{ style: { minWidth: "1000px" } }}>
      <DialogTitle sx={{ fontWeight: 'bolder' }}>Upload Edit</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: "rgba(63, 66, 66, 0.76)", whiteSpace: "pre-line" }}>
          <Stack spacing={2} direction="row" mt={1} mb={2}>
            <TextField
              variant='outlined'
              color='primary'
              label="Park Name"
              disabled={!parks.length}
              onChange={e => handleChange('parkName', e.target.value)}
              value={form.parkName || ''}
              InputLabelProps={{shrink: true}}
              sx={{ width: 600 }}
              required
              select
            >
              {parks.map(opt => (
                <MenuItem key={opt.name} value={opt.name}>{opt.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              type="text"
              variant='outlined'
              color='secondary'
              label="Park Code"
              value={form.parkCode || ''}
              disabled
              InputLabelProps={{shrink: true}}
            />

            <TextField
              type="text"
              variant='outlined'
              color='secondary'
              label="Region"
              value={form.parkRegionName || ''}
              disabled
            />

            <TextField
              type="text"
              variant='outlined'
              color='secondary'
              label="Park Previous Names"
              value={form.parkPreviousName || ''}
              disabled
            />
          </Stack>

          <Stack spacing={2} direction="row" mb={2}>
            <TextField
              type="text"
              variant='outlined'
              color='secondary'
              label="Title"
              onChange={e => handleChange('title', e.target.value)}
              sx={{width: 400}} 
              value={form.title || ''}
              required              
              InputLabelProps={{shrink: form.title}}
            />

            <TextField
              type="text"
              variant='outlined'
              color='primary'
              label="Proposed Security Type"
              onChange={e => handleChange('docType', e.target.value)}
              value={
                //Fallback look up for Doc Type Id
                form?.docTypeId !== null && form?.docTypeId !== undefined 
                  ? form?.docTypeId 
                  : DOC_TYPES.find(x => x.label == form?.docType 
                                  || x.code == form?.docType)?.id ?? ""
              }
              InputLabelProps={{shrink: true}}
              sx={{width: 500}} 
              required
              select>
              {securityTypes.map((option) => (
                <MenuItem key={option.label} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="text"
              variant='outlined'
              color='secondary'
              label="TIC Number"
              onChange={e => handleChange('ticNumber', e.target.value.replace(/\D/g, ''))}
              value={form.ticNumber || ''}
              InputLabelProps={{shrink: form.ticNumber}}
              inputProps={{ maxLength: 6 }}
            />

            <TextField
              type="text"
              variant='outlined'
              color='secondary'
              label="PMIS #"
              onChange={e => handleChange('pmisNumber', e.target.value.replace(/\D/g, ''))}
              value={form.pmisNumber || ''}
              inputProps={{ maxLength: 6 }}
            />
          </Stack>

          <TextField
            variant='outlined'
            color='primary'
            label="Comments For TIC Staff"
            onChange={e => handleChange('comments', e.target.value)}
            value={form?.comments || ''}
            size="small"
            sx={{width: 800}}
            InputLabelProps={{shrink: form.comments}}
            multiline
          />
          
          <Stack spacing={2} direction="row" sx={{marginBottom: 2, marginTop: 2}}>
          {images.length ? (
            <ImageList sx={{width: '100%'}}>
              {images.map(child => (
                <ImageListItem key={child.AssetID} sx={{ cursor: 'pointer' }}>
                  <img
                    src={`https://npgallery.nps.gov/GetAsset/${child.AssetID}`}
                    alt={child.pdfName}
                    loading="lazy"
                    onClick={() => handleImageClick(child.AssetID)}
                  />
                  <ImageListItemBar
                    title={child.pdfName}
                    subtitle={`Size: ${child.pdfSize}`}
                    actionIcon={
                      <IconButton onClick={() => handleImageClick(child.AssetID)}
                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                              aria-label={`View ${parent?.children[0]?.pdfName}`}>
                        <FontAwesomeIcon icon={["fas","info-circle"]} />
                      </IconButton>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          ) : (
            <Button variant="outlined" startIcon={<FontAwesomeIcon icon={["fas","download"]} />} onClick={
              async () => {
              try {
                const response = await axios.get(
                  `https://npgallery.nps.gov/api/ETIC/DownloadUserUpload`,
                  {
                    params: { id: row.id, filename: row.fileName },
                    headers: {
                      Authorization: `Bearer ${token}`
                    },
                    responseType: 'blob'
                  }
                );

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                const contentDisposition = response.headers["Content-Disposition"] || "";
                let fileName = "downloaded-file";

                const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i);

                if (match) {
                  fileName = decodeURIComponent(match[1] || match[2] || match[3]);
                }

                link.setAttribute('download', row.fileName || fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } catch (err) {
                console.error("Download failed:", err);
              }
            }}>
              Download File
            </Button>
          )}
          </Stack>
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseClick} variant="outlined" startIcon={<FontAwesomeIcon icon={["fas","circle-x"]} />}>Cancel</Button>
        {row.status !== 'Rejected' && row.status !== 'Complete' ? (
          <Button variant="outlined" color="error" onClick={handleRejectUploadDialogClick} startIcon={<FontAwesomeIcon icon={["fas","trash"]} />}>Reject</Button>
        ) : (
          <Button variant="outlined" color="error" onClick={handleUnRejectClick} startIcon={<FontAwesomeIcon icon={["fas","trash"]} />}>UnReject</Button>
        )}
        {row.status !== 'Rejected' && row.status !== 'Complete' && (
          <Button
            variant="outlined"
            onClick={() => handleSaveClick(form, row)}
            startIcon={<FontAwesomeIcon icon={["fas","save"]} />}
            disabled={!form?.parkName || !form?.title || !form?.docType}
          >Continue Cataloging</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
